import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Row, Col, Typography, Card, Tag } from 'antd';
import { KeyOutlined, PlusOutlined, UserOutlined, EditOutlined, SafetyOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import { getUserData } from '../../constants';

const { Title, Text } = Typography;

const userData = getUserData();
const rolePermission = userData && userData?.user ? userData?.user?.role?.role_permission : {};

const Roles = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/all-roles');
            const filteredRoles = (response.data.roles || []).filter(role => {
                if (!role.name) return true;
                return role.name.toLowerCase() !== 'system owner' || rolePermission.is_system_owner === 1;
            });
            setRoles(filteredRoles);
        } catch (error) {
            message.error("Failed to load roles.");
        } finally {
            setLoading(false);
        }
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const response = await axiosInstance.post('/roles', { name: values.name });
            setRoles([...roles, response.data.role]);
            message.success("Role created successfully");
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            if (error.response && error.response.data.errors) {
                message.error(error.response.data.errors.name[0]);
            } else {
                message.error("Failed to create role");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
        ],
    };

    const columns = [
        { 
            title: 'Role Name', 
            dataIndex: 'name', 
            key: 'name', 
            render: (text) => {
                const isAdmin = text?.toLowerCase() === 'admin';
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SafetyOutlined style={{ marginRight: 8, color: isAdmin ? '#1890ff' : '#722ed1' }} />
                        <Text strong style={{ fontSize: '14px' }}>{text}</Text>
                    </div>
                );
            }
        },
        {
            title: 'Badge Type',
            key: 'badge',
            render: (_, record) => {
                const name = record.name?.toLowerCase();
                const color = name === 'admin' ? 'blue' : name === 'manager' ? 'cyan' : 'purple';
                return (
                    <Tag color={color} style={{ borderRadius: '4px', textTransform: 'capitalize' }}>
                        {record.name} Authorization
                    </Tag>
                );
            },
            width: 200
        }
    ];

    return (
        <div style={{ padding: '4px' }}>
            {/* Header Banner */}
            <div className="welcome-banner-card" style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Title level={2} className="welcome-title" style={{ margin: 0, fontWeight: '800', letterSpacing: '-0.5px' }}>
                            User Roles Registry
                        </Title>
                        <Text className="welcome-subtitle" style={{ fontSize: '15px', display: 'block', marginTop: '6px' }}>
                            Define organizational roles to classify authorizations and permissions across the application.
                        </Text>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            size="large" 
                            icon={<PlusOutlined />} 
                            onClick={showModal}
                            style={{ borderRadius: '8px', boxShadow: '0 4px 10px rgba(24, 144, 255, 0.25)' }}
                        >
                            Add New Role
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Roles Table */}
            <Card className="dashboard-card" bordered={false} bodyStyle={{ padding: '24px' }}>
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={roles?.map(role => ({ key: role.id, id: role.id, name: role.name })) || []}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 'max-content' }} 
                    loading={loading}
                />
            </Card>

            {/* Add Role Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: '700' }}>
                        <KeyOutlined style={{ color: '#1890ff' }} />
                        <span>Create New System Role</span>
                    </div>
                }
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                confirmLoading={loading}
                okText="Create Role"
                cancelText="Cancel"
                destroyOnClose
                bodyStyle={{ padding: '24px' }}
                style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
                <Form form={form} layout="vertical" onFinish={handleOk}>
                    <Form.Item
                        name="name"
                        label={<Text strong>Role Name</Text>}
                        rules={[{ required: true, message: 'Please input the role name!' }]}
                    >
                        <Input placeholder="e.g. Supervisor" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Roles;
